import React, { useState } from 'react';
import {
  Box, Typography, Checkbox, IconButton
} from '@mui/material';
import {
  FolderOpen as FolderIcon
} from '@mui/icons-material';

const FolderTreeView = ({ folderOptions, selectedFolders, setSelectedFolders }) => {
  // Açık klasörleri takip etmek için state
  const [expandedFolders, setExpandedFolders] = useState({});
  
  // Klasör yapısını oluştur
  const buildFolderTree = (folderOptions) => {
    // Ana klasörleri ve alt klasörleri grupla
    const parentMap = {};
    
    // Önce ana haritayı oluştur
    folderOptions.forEach(folder => {
      const parentId = folder.parentId || 0;
      if (!parentMap[parentId]) {
        parentMap[parentId] = [];
      }
      parentMap[parentId].push(folder);
    });
    
    // Sadece üst seviye klasörleri döndür (parentId = 0 olanlar)
    return parentMap[0] || [];
  };
  
  // Bir klasörün alt klasörlerini getir
  const getChildren = (parentId, folderOptions) => {
    return folderOptions.filter(folder => {
      const folderParentId = folder.parentId || 0;
      return folderParentId.toString() === parentId.toString();
    });
  };
  
  // Klasörü aç/kapat
  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };
  
  // Checkbox değişimi - Multiple selection
  const handleFolderSelect = (folderId) => {
    console.log('📁 FOLDER SELECTION DEBUG - folderId:', folderId);
    console.log('📁 FOLDER SELECTION DEBUG - folderId type:', typeof folderId);
    console.log('📁 FOLDER SELECTION DEBUG - current selectedFolders:', selectedFolders);
    
    setSelectedFolders(prev => {
      if (prev.includes(folderId)) {
        const newSelection = prev.filter(id => id !== folderId);
        console.log('📁 FOLDER SELECTION DEBUG - removing folder, new selection:', newSelection);
        return newSelection;
      } else {
        const newSelection = [...prev, folderId];
        console.log('📁 FOLDER SELECTION DEBUG - adding folder, new selection:', newSelection);
        return newSelection;
      }
    });
  };
  
  // Bir klasörün alt klasörleri var mı kontrol et
  const hasChildren = (folderId, folderOptions) => {
    return folderOptions.some(folder => {
      const folderParentId = folder.parentId || 0;
      return folderParentId.toString() === folderId.toString();
    });
  };
  
  // Recursive olarak klasör yapısını render et
  const renderFolderItem = (folder) => {
    const hasSubfolders = hasChildren(folder.value, folderOptions);
    const isExpanded = expandedFolders[folder.value];
    const isSelected = selectedFolders.includes(folder.value);
    const children = getChildren(folder.value, folderOptions);
    
    return (
      <Box key={folder.value} sx={{ mb: 1 }}>
        <Box 
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover'
            },
            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
            border: isSelected ? '1px solid' : '1px solid transparent',
            borderColor: isSelected ? 'primary.main' : 'transparent',
          }}
          onClick={() => handleFolderSelect(folder.value)}
        >
          {hasSubfolders ? (
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.value);
              }}
              sx={{ mr: 0.5 }}
            >
              {isExpanded ? (
                <Box sx={{ color: 'text.secondary', fontSize: 18 }}>▼</Box>
              ) : (
                <Box sx={{ color: 'text.secondary', fontSize: 18 }}>▶</Box>
              )}
            </IconButton>
          ) : (
            <Box sx={{ width: 28 }} /> // Boşluk tutucu
          )}
          
          <Checkbox 
            checked={isSelected}
            onChange={() => handleFolderSelect(folder.value)}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
          
          <FolderIcon 
            sx={{ 
              ml: 0.5, 
              mr: 1.5, 
              color: isSelected ? 'primary.main' : 'action.active' 
            }} 
          />
          
          <Typography 
            variant="body2"
            sx={{ 
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? 'primary.main' : 'text.primary'
            }}
          >
            {folder.label}
          </Typography>
        </Box>
        
        {/* Alt klasörleri render et (açıksa) */}
        {isExpanded && hasSubfolders && (
          <Box sx={{ pl: 4 }}>
            {children.map(childFolder => renderFolderItem(childFolder))}
          </Box>
        )}
      </Box>
    );
  };
  
  // Ana klasörleri bul
  const rootFolders = buildFolderTree(folderOptions);
  
  return (
    <Box 
      sx={{ 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        maxHeight: '400px',
        overflow: 'auto'
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Klasör Yapısı (Çoklu Seçim)
      </Typography>
      
      {/* Ana Klasör seçeneği */}
      <Box sx={{ mb: 2 }}>
        <Box 
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover'
            },
            bgcolor: selectedFolders.includes('0') ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
            border: selectedFolders.includes('0') ? '1px solid' : '1px solid transparent',
            borderColor: selectedFolders.includes('0') ? 'primary.main' : 'transparent',
          }}
          onClick={() => handleFolderSelect('0')}
        >
          <Box sx={{ width: 28 }} />
          <Checkbox 
            checked={selectedFolders.includes('0')}
            onChange={() => handleFolderSelect('0')}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
          <FolderIcon 
            sx={{ 
              ml: 0.5, 
              mr: 1.5, 
              color: selectedFolders.includes('0') ? 'primary.main' : 'action.active' 
            }} 
          />
          <Typography 
            variant="body2"
            sx={{ 
              fontWeight: selectedFolders.includes('0') ? 600 : 400,
              color: selectedFolders.includes('0') ? 'primary.main' : 'text.primary'
            }}
          >
            Ana Klasör
          </Typography>
        </Box>
      </Box>
      
      {rootFolders.map(folder => renderFolderItem(folder))}
    </Box>
  );
};

export default FolderTreeView;
